import { DataResult, State as DataState } from '@progress/kendo-data-query';
import {
  GridCellProps,
  GridDataStateChangeEvent,
} from '@progress/kendo-react-grid';
import { produce } from 'immer';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useSearchParams } from 'react-router-dom';
import {
  formatDate,
  showErrorToast,
} from '../../../../../common/global/helpers';
import { LoadingState } from '../../../../../common/global/models';
import Breadcrumbs from '../../../../../common/widgets/Breadcrumbs';
import Button from '../../../../../common/widgets/Button';
import { DataLoader } from '../../../../../common/widgets/DataLoader';
import Grid, { GridCellTd, getSkip } from '../../../../../common/widgets/Grid';
import Icons from '../../../../../common/widgets/Icons';
import Loader from '../../../../../common/widgets/Loader';
import LoadingSpinner from '../../../../../common/widgets/LoadingSpinner';
import PopoverDialog from '../../../../../common/widgets/PopoverDialog';
import { config } from '../../../config';
import { CustomerInfoContext } from '../../../contexts/CustomerInfoContext';
import { NotificationItem } from '../../../models/Notifications';
import { resources } from '../../../resources/resources';
import { notificationService } from '../../../services/notification';
import applicationCrumbs from '../../ApplicationCrumbs';
import { EmptyStateNotifications } from '../../Common/EmptyStates';
import PlatformInformation from '../../Common/PlatformInformation';
import MyAccountMenu from '../Common/MyAccountMenu';

interface NotificationsPageState {
  notifications?: NotificationItem[];
  total: number;
  markAllAsReadButtonState?: LoadingState;
  visiblePopoverId?: string;
  dataState: DataState;
  showErrorPanel?: boolean;
}

const formatNotifications = (data: NotificationItem[]) => {
  return data.map(e => ({
    ...e,
    dateSent: formatDate(e.dateSent),
  }));
};

const initialDataState = {
  skip: 0,
  take: config.grid.defaultPageSize,
  sort: [{ field: 'dateSent', dir: 'desc' }],
};

export const NotificationsPage = () => {
  const { t: translate } = useTranslation();
  const crumbs = applicationCrumbs(translate);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const odataString = searchParams.get('dataState');
  const [state, setState] = useState<NotificationsPageState>({
    total: 0,
    dataState: odataString
      ? JSON.parse(decodeURI(odataString))
      : initialDataState,
  });
  const { customerInfo, setCustomerInfo } = useContext(CustomerInfoContext);
  const [refreshData, setRefreshData] = useState(false);

  const dataStateChange = (e: GridDataStateChangeEvent) => {
    navigate(
      `/my-account/notifications?dataState=${encodeURI(
        JSON.stringify({
          ...e.dataState,
          skip: getSkip(state.dataState, e.dataState),
        }),
      )}`,
    );
  };

  const goBackOnePage = () => {
    navigate(
      `/my-account/notifications?dataState=${encodeURI(
        JSON.stringify({
          ...state.dataState,
          skip: (state.dataState.skip ?? 0) - (state.dataState.take ?? 0),
        }),
      )}`,
    );
  };

  useEffect(() => {
    setState(
      produce(d => {
        d.dataState = odataString
          ? JSON.parse(decodeURI(odataString))
          : initialDataState;
      }),
    );
  }, [odataString]);

  const onDataReceived = (data: DataResult, showErrorPanel?: boolean) => {
    setState(
      produce(draft => {
        draft.notifications = formatNotifications(data.data);
        draft.total = data.total;
        draft.showErrorPanel = showErrorPanel;
      }),
    );
  };

  const markAllAsRead = async () => {
    setState(
      produce(draft => {
        draft.markAllAsReadButtonState = LoadingState.Requested;
      }),
    );
    const response = await notificationService.markAllAsRead();
    setState(
      produce(draft => {
        draft.markAllAsReadButtonState = LoadingState.Ready;
      }),
    );

    if (!response.hasErrors) {
      setState(
        produce(draft => {
          draft.notifications = draft.notifications?.map(e => ({
            ...e,
            isRead: true,
          }));
          draft.markAllAsReadButtonState = LoadingState.Ready;
        }),
      );
      setCustomerInfo(
        produce(customerInfo, draft => {
          draft.notificationsCount = 0;
        }),
      );
    } else {
      showErrorToast(translate(resources.errors.generic));
    }
  };

  const markAsRead = async (notification: NotificationItem) => {
    const response = await notificationService.markAsRead(
      notification.notificationId,
    );

    if (!response.hasErrors) {
      setState(
        produce(draft => {
          const n = draft.notifications?.find(
            e => e.notificationId === notification.notificationId,
          );
          if (n) {
            n.isRead = true;
          }
        }),
      );
      setCustomerInfo(
        produce(customerInfo, draft => {
          draft.notificationsCount -= 1;
        }),
      );
    } else {
      showErrorToast(translate(resources.errors.generic));
    }
  };

  const deleteNotification = async (notification: NotificationItem) => {
    const decreaseNotificationsCount = !notification.isRead;

    const response = await notificationService.deleteNotification(
      notification.notificationId,
    );

    if (!response.hasErrors) {
      if (state.notifications?.length === 1 && state.dataState.skip) {
        goBackOnePage();
      }
      setRefreshData(true);
      if (decreaseNotificationsCount) {
        setCustomerInfo(
          produce(customerInfo, draft => {
            draft.notificationsCount -= 1;
          }),
        );
      }
    } else {
      showErrorToast(translate(resources.errors.generic));
    }
  };

  useEffect(() => {
    // to refresh data only once
    if (refreshData) {
      setRefreshData(false);
    }
  }, [refreshData]);

  return (
    <>
      <Breadcrumbs
        entries={[crumbs.home, crumbs.accountDashboard, crumbs.notifications]}
      />
      <section className="section-main section-actions my-account-page notifications-page">
        <div className="container">
          <div className="area-actions">
            <MyAccountMenu />
          </div>
          <div className="area-content">
            <PlatformInformation />
            <div className="panel padding-xl">
              <div className="page-actions">
                <h1>{translate(resources.notifications.title)}</h1>
                {!!state?.notifications?.length && (
                  <div className="page-action">
                    <Button
                      loading={
                        state.markAllAsReadButtonState ===
                        LoadingState.Requested
                      }
                      onClick={markAllAsRead}
                      className="btn btn-solid btn-sm"
                      icon={<Icons.MarkAll />}
                    >
                      <span className="btn-text">
                        {translate(resources.notifications.markAllAsRead)}
                      </span>
                    </Button>
                  </div>
                )}
              </div>
              <DataLoader
                getData={notificationService.getNotifications}
                dataState={state.dataState}
                onDataReceived={onDataReceived}
                bypassRequestDataIfNeeded={refreshData}
              />
              <Loader
                loading={!state?.notifications}
                loader={<LoadingSpinner />}
                showErrorPanel={state.showErrorPanel}
              >
                {state?.notifications?.length ? (
                  <div className="notifications-list">
                    <Grid
                      data={state.notifications ?? []}
                      hideHeaderRow
                      pageable={{
                        pageSizes: config.grid.pageSizes,
                        pageSizeValue: state.dataState.take,
                        info: false,
                        previousNext: true,
                      }}
                      {...state.dataState}
                      onDataStateChange={dataStateChange}
                      total={state.total}
                      columns={[
                        {
                          field: 'body',
                          cell: BodyCell,
                        },
                        {
                          field: 'dateSent',
                          width: 150,
                          cell: DateSentCell,
                        },
                        {
                          width: 100,
                          headerClassName: 'grid-actions-cell',
                          cell: props => (
                            <ActionsCell
                              {...props}
                              markAsRead={markAsRead}
                              deleteNotification={deleteNotification}
                            />
                          ),
                        },
                      ]}
                    />
                  </div>
                ) : (
                  <EmptyStateNotifications />
                )}
              </Loader>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

const UnreadNotificationBody = ({ body }: { body: string }) => (
  <div className="message-wrapper">
    <div className="message-indicator msg-unread"></div>
    <div className="msg-unread">{body}</div>
  </div>
);

const ReadNotificationBody = ({ body }: { body: string }) => (
  <div className="message-wrapper">
    <div className="message-indicator msg-read"></div>
    <div className="msg-read">{body}</div>
  </div>
);

const BodyCell = ({ dataItem }: GridCellProps) => (
  <GridCellTd>
    {dataItem.isRead ? (
      <ReadNotificationBody body={dataItem.body} />
    ) : (
      <UnreadNotificationBody body={dataItem.body} />
    )}
  </GridCellTd>
);

const DateSentCell = ({ dataItem }: GridCellProps) => (
  <GridCellTd className="notification-date">
    {dataItem.isRead ? (
      <div className="msg-read">{dataItem.dateSent}</div>
    ) : (
      <div className="msg-unread">{dataItem.dateSent}</div>
    )}
  </GridCellTd>
);

interface ActionsCellProps extends GridCellProps {
  markAsRead: (notification: NotificationItem) => Promise<void>;
  deleteNotification: (notification: NotificationItem) => Promise<void>;
}

const ActionsCell = ({
  dataItem,
  markAsRead,
  deleteNotification,
}: ActionsCellProps) => {
  const { t: translate } = useTranslation();
  const id = `span${dataItem.notificationId}`;

  return (
    <GridCellTd className="grid-actions-cell">
      <PopoverDialog
        className="popover-actions-list"
        placement="bottom"
        closeFromContent
        type="click"
        appendTo={document.body}
        content={
          <>
            {!dataItem.isRead && (
              <button className="btn" onClick={() => markAsRead(dataItem)}>
                {translate(resources.notifications.markAsRead)}
              </button>
            )}

            <button
              className="btn"
              onClick={() => deleteNotification(dataItem)}
            >
              {translate(resources.notifications.delete)}
            </button>
          </>
        }
      >
        <a id={id} className="notification-action"></a>
      </PopoverDialog>
    </GridCellTd>
  );
};
